import { manufacturingFetch } from "@/lib/manufacturingClient";
import { requireSupabase } from "@/lib/supabase";
import type {
  ClassifySdsNeedInput,
  SdsClassificationResult,
} from "@/lib/manufacturing/msdsClassify";
import type {
  AcknowledgeMaterialSdsOpenPayload,
  GenerateMaterialSdsDraftPayload,
  MaterialSdsDetailResponse,
  PublishMaterialSdsPayload,
  SaveMaterialSdsDraftPayload,
  SaveMaterialSdsDraftResponse,
  MfgMaterialSdsDocument,
} from "@/lib/manufacturing/msdsDocumentTypes";
import type { MfgSdsBinderPreviewResponse } from "@/lib/manufacturing/msdsBinderTypes";

export type ClassifySdsNeedResponse = {
  ok: true;
  classification: SdsClassificationResult;
  outcome: string;
};

export function classifySdsNeed(payload: ClassifySdsNeedInput) {
  return manufacturingFetch<ClassifySdsNeedResponse>("/api/mfg/materials/classify-sds-need", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getMaterialSdsDetail(materialId: string, plantId?: string) {
  const qs = new URLSearchParams({ material_id: materialId });
  if (plantId) qs.set("plant_id", plantId);
  return manufacturingFetch<MaterialSdsDetailResponse>(
    `/api/mfg/materials/sds/detail?${qs.toString()}`,
    { method: "GET" }
  );
}

export function saveMaterialSdsDraft(payload: SaveMaterialSdsDraftPayload) {
  return manufacturingFetch<SaveMaterialSdsDraftResponse>("/api/mfg/materials/sds/save-draft", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function generateMaterialSdsDraft(payload: GenerateMaterialSdsDraftPayload) {
  return manufacturingFetch<{ ok: true; document: MfgMaterialSdsDocument }>(
    "/api/mfg/materials/sds/generate-draft",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function acknowledgeMaterialSdsOpen(payload: AcknowledgeMaterialSdsOpenPayload) {
  return manufacturingFetch<{ ok: true; document: MfgMaterialSdsDocument }>(
    "/api/mfg/materials/sds/acknowledge-open",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function getMaterialSdsBinderPreview(plantId?: string) {
  const qs = new URLSearchParams();
  if (plantId) qs.set("plant_id", plantId);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return manufacturingFetch<MfgSdsBinderPreviewResponse>(
    `/api/mfg/materials/sds/binder-preview${suffix}`,
    { method: "GET" }
  );
}

export function publishMaterialSds(payload: PublishMaterialSdsPayload) {
  return manufacturingFetch<{ ok: true; document: MfgMaterialSdsDocument }>(
    "/api/mfg/materials/sds/publish",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

type SdsSupplierSignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  attachment_id: string;
  material_id: string;
  plant_id: string;
  content_type: string;
};

function isSdsSupplierSignOk(x: unknown): x is SdsSupplierSignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.attachment_id === "string" &&
    typeof o.material_id === "string" &&
    typeof o.content_type === "string"
  );
}

/** Sign → browser upload → commit supplier SDS PDF (receipt pattern). */
export async function uploadMaterialSdsSupplierPdf(args: {
  materialId: string;
  file: File;
}): Promise<{ ok: true; document: MfgMaterialSdsDocument } | { ok: false; error: string }> {
  const contentType = (args.file.type || "application/pdf").toLowerCase();
  if (contentType !== "application/pdf") {
    return { ok: false, error: "Supplier SDS must be a PDF." };
  }

  const sign = await manufacturingFetch<unknown>("/api/mfg/materials/sds/upload-supplier", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      material_id: args.materialId,
      content_type: contentType,
      byte_size: args.file.size,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSdsSupplierSignOk(sign.data)) {
    const errCandidate = (sign.data as { error?: unknown })?.error;
    return {
      ok: false,
      error: typeof errCandidate === "string" ? errCandidate : "Sign response was malformed.",
    };
  }
  const s = sign.data;
  const sb = requireSupabase();
  const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(
    s.path,
    s.token,
    args.file,
    { contentType: s.content_type, upsert: false }
  );
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await manufacturingFetch<{ ok: true; document: MfgMaterialSdsDocument }>(
    "/api/mfg/materials/sds/upload-supplier",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op: "commit",
        material_id: s.material_id,
        attachment_id: s.attachment_id,
        object_path: s.path,
        content_type: s.content_type,
        byte_size: args.file.size,
      }),
    }
  );
  if (!commit.ok) return { ok: false, error: commit.error };
  return { ok: true, document: commit.data.document };
}
